import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  ShieldCheck,
  Trash2,
  RefreshCw,
  Check,
  X,
  Key
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { user: currentUser, token } = useAuth();

  // New user form
  const [newUser, setNewUser] = useState({
    email: "",
    first_name: "",
    last_name: "",
    role: "user"
  });
  const [creating, setCreating] = useState(false);
  const [initialPassword, setInitialPassword] = useState("");

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error("Fehler beim Laden der Benutzer");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.first_name || !newUser.last_name) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return;
    }

    setCreating(true);
    try {
      const res = await axios.post(`${API}/users`, newUser);
      toast.success(res.data.message);
      
      if (res.data.initial_password) {
        setInitialPassword(res.data.initial_password);
      } else {
        setCreateDialogOpen(false);
        setNewUser({ email: "", first_name: "", last_name: "", role: "user" });
      }
      
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Erstellen");
    } finally {
      setCreating(false);
    }
  };

  const handleResetPassword = async (userId, userName) => {
    try {
      const res = await axios.post(`${API}/users/${userId}/reset-password`);
      toast.success(res.data.message);
      
      if (res.data.new_password) {
        toast.info(`Neues Passwort für ${userName}: ${res.data.new_password}`, { duration: 10000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Zurücksetzen");
    }
  };

  const handleToggleActive = async (userId, currentActive) => {
    try {
      await axios.put(`${API}/users/${userId}`, { is_active: !currentActive });
      toast.success(currentActive ? "Benutzer deaktiviert" : "Benutzer aktiviert");
      fetchUsers();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}`, { role: newRole });
      toast.success("Rolle aktualisiert");
      fetchUsers();
    } catch (err) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success("Benutzer gelöscht");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-2xl font-heading font-bold text-slate-800">
            Benutzerverwaltung
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Benutzer anlegen, bearbeiten und verwalten
          </p>
        </div>
        
        <Dialog open={createDialogOpen} onOpenChange={(open) => {
          setCreateDialogOpen(open);
          if (!open) {
            setNewUser({ email: "", first_name: "", last_name: "", role: "user" });
            setInitialPassword("");
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-rose-600 hover:bg-rose-700">
              <UserPlus className="w-4 h-4 mr-2" />
              Neuer Benutzer
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Neuen Benutzer anlegen</DialogTitle>
            </DialogHeader>
            
            {initialPassword ? (
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 mb-2">
                    E-Mail konnte nicht gesendet werden. Bitte teilen Sie dem Benutzer das Passwort manuell mit:
                  </p>
                  <div className="flex items-center gap-2 p-2 bg-white rounded border">
                    <Key className="w-4 h-4 text-amber-600" />
                    <code className="text-lg font-mono font-bold">{initialPassword}</code>
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={() => {
                    setCreateDialogOpen(false);
                    setInitialPassword("");
                    setNewUser({ email: "", first_name: "", last_name: "", role: "user" });
                  }}>
                    Schließen
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vorname *</Label>
                    <Input
                      value={newUser.first_name}
                      onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                      placeholder="Max"
                    />
                  </div>
                  <div>
                    <Label>Nachname *</Label>
                    <Input
                      value={newUser.last_name}
                      onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                      placeholder="Mustermann"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>E-Mail-Adresse *</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    placeholder="max.mustermann@feuerwehr.de"
                  />
                </div>
                
                <div>
                  <Label>Rolle</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Benutzer</SelectItem>
                      <SelectItem value="admin">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <p className="text-sm text-slate-500">
                  Das initiale Passwort wird automatisch generiert und per E-Mail an den Benutzer gesendet.
                  Der Benutzer muss das Passwort bei der ersten Anmeldung ändern.
                </p>
                
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="outline">Abbrechen</Button>
                  </DialogClose>
                  <Button 
                    onClick={handleCreateUser}
                    disabled={creating || !newUser.email || !newUser.first_name || !newUser.last_name}
                    className="bg-rose-600 hover:bg-rose-700"
                  >
                    {creating ? "Erstelle..." : "Benutzer anlegen"}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </motion.div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Users className="w-5 h-5" />
            Benutzer
            <Badge variant="secondary">{users.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-slate-500">Lade Benutzer...</div>
          ) : users.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">Noch keine Benutzer angelegt</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>E-Mail</TableHead>
                    <TableHead>Rolle</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Letzte Anmeldung</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} className={!u.is_active ? "opacity-50" : ""}>
                      <TableCell className="font-medium">
                        {u.first_name} {u.last_name}
                        {u.must_change_password && (
                          <Badge variant="outline" className="ml-2 text-amber-600">
                            PW ändern
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">{u.email}</TableCell>
                      <TableCell>
                        <Select 
                          value={u.role} 
                          onValueChange={(v) => handleChangeRole(u.id, v)}
                          disabled={u.id === currentUser?.id}
                        >
                          <SelectTrigger className="w-32 h-8">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">
                              <div className="flex items-center gap-2">
                                <Shield className="w-3 h-3" />
                                Benutzer
                              </div>
                            </SelectItem>
                            <SelectItem value="admin">
                              <div className="flex items-center gap-2">
                                <ShieldCheck className="w-3 h-3" />
                                Admin
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => handleToggleActive(u.id, u.is_active)}
                          disabled={u.id === currentUser?.id}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                            u.is_active 
                              ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" 
                              : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                          }`}
                        >
                          {u.is_active ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                          {u.is_active ? "Aktiv" : "Inaktiv"}
                        </button>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {u.last_login 
                          ? new Date(u.last_login).toLocaleString("de-DE")
                          : "Nie"
                        }
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleResetPassword(u.id, `${u.first_name} ${u.last_name}`)}
                            title="Passwort zurücksetzen"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          
                          {u.id !== currentUser?.id && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Benutzer löschen?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Möchten Sie {u.first_name} {u.last_name} wirklich löschen? 
                                    Diese Aktion kann nicht rückgängig gemacht werden.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => handleDeleteUser(u.id)}
                                    className="bg-rose-600 hover:bg-rose-700"
                                  >
                                    Löschen
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersPage;
